import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnConfig, ProcessedApiKey, ProcessedBefel, BefelFilterStatus, BefelFilterConfig, EmirTipi, CurrencyType } from '../types';
import { CURRENCY_SYMBOLS } from '../types';
import { ApiKeysManager } from '../components/ApiKeysManager';
import { FilterTabs } from '../components/FilterTabs';
import { DataTable } from '../components/DataTable';
import { Tooltip } from '../components/Tooltip';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import { apiKeysApi, befelsApi } from '../services/api';
import styles from './DashboardPage.module.css';

// Search filter interface for Emirler
interface EmirlerFilters {
  urunAdi: string;
  modelKodu: string;
  sku: string;
}

// Platform display names
const PLATFORM_NAMES: Record<string, string> = {
  'TRENDYOL': 'Trendyol',
  'N11': 'N11',
  'AMAZON': 'Amazon',
  'HEPSIBURADA': 'Hepsiburada',
};

// Platform seller portal URLs
const PLATFORM_URLS: Record<string, string> = {
  'TRENDYOL': 'https://partner.trendyol.com',
  'N11': 'https://so.n11.com',
  'AMAZON': 'https://satis.amazon.com.tr',
  'AMAZONTR': 'https://satis.amazon.com.tr',
  'HEPSIBURADA': 'https://merchant.hepsiburada.com',
};

// Deactivation reason codes (matches DeaktivasyonNedenleri in backend)
const DEACTIVATION_REASONS: Record<number, string> = {
  0: 'Aktif',
  1: 'Üyelik Bitti',
  2: 'Sayfa Yok',
  3: 'Ürün Yok',
  4: 'API Hatası',
  5: 'Kullanıcı Durdurdu',
  6: 'Limit Aşımı',
  7: 'API Anahtarı Bitti',
  8: 'Fiyat Değişti',
  10: 'Pazar Yoğunluğu',  // Market standby - busy market
  99: 'Belirsiz Hata',
};

// Special deactivation reason for market standby (busy market conditions)
const DEAKTIVATION_CONSENSUS = 10;

export function DashboardPage() {
  const [befels, setBefels] = useState<ProcessedBefel[]>([]);
  const [apiKeys, setApiKeys] = useState<ProcessedApiKey[]>([]);
  const { token, user, refreshUser } = useAuth();
  const [activeFilter, setActiveFilter] = useState<BefelFilterStatus>('all');
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();

  // Search filters for Emirler
  const [emirlerFilters, setEmirlerFilters] = useState<EmirlerFilters>({
    urunAdi: '',
    modelKodu: '',
    sku: '',
  });

  // Pagination state for Emirler
  const [emirlerCurrentPage, setEmirlerCurrentPage] = useState(1);
  const [emirlerPerPage, setEmirlerPerPage] = useState(20);
  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

  // Selection state for mass actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isMassActionLoading, setIsMassActionLoading] = useState(false);

  // Refresh user data on dashboard load to get latest membership info
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Private page - noindex
  useSEO({
    title: 'Dashboard',
    description: 'Polaris Dashboard',
    noindex: true,
  });
  const [orderCounts, setOrderCounts] = useState({ current: 0, max: 0 });

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBefel, setEditingBefel] = useState<ProcessedBefel | null>(null);
  const [editFormData, setEditFormData] = useState({
    alt: '',
    ust: '',
    emirTipi: 0 as EmirTipi,
    paraTipi: 0 as CurrencyType,
    priceIncludesCommission: true,
    activateCoupon: false,
    activatePromotion: false,
  });
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editMessage, setEditMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      if (token) {
        // Fetch real API keys from server
        const apiKeysResponse = await apiKeysApi.getAll(token);
        if (apiKeysResponse.data && apiKeysResponse.data.apiKeys) {
          const now = new Date();
          const processedKeys: ProcessedApiKey[] = apiKeysResponse.data.apiKeys.map((k) => {
            const expiresAt = k.expiresAt ? new Date(k.expiresAt) : null;
            const daysRemaining = expiresAt
              ? Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
              : 0;

            return {
              id: k.id,
              platform: k.platform,
              platformDisplayName: PLATFORM_NAMES[k.platform] || k.platform,
              keyType: k.keyType,
              sellerId: k.sellerId,
              expiresAt,
              daysRemaining: Math.max(0, daysRemaining),
              isActive: k.isActive && (expiresAt ? daysRemaining > 0 : true),
            };
          });
          setApiKeys(processedKeys);
        }

        // Fetch real Befels (Emirler) from server
        const befelsResponse = await befelsApi.getAll(token, true);
        if (befelsResponse.data && befelsResponse.data.befels) {
          const processedBefels: ProcessedBefel[] = befelsResponse.data.befels.map((b) => {
            // Determine status based on active, workerID, and deactivation reason
            // Special case: Market standby befels (reason 10) show as 'working' even though active=false
            let status: 'working' | 'waiting' | 'inactive';
            if (!b.active) {
              if (b.deaktivasyonNedeni === DEAKTIVATION_CONSENSUS) {
                // Market standby - busy market, show as working (reason column shows Pazar Yoğunluğu)
                status = 'working';
              } else {
                status = 'inactive';
              }
            } else if (b.workerID > 0) {
              status = 'working';
            } else {
              status = 'waiting';
            }

            return {
              id: b.id,
              platform: b.platform,
              platformDisplayName: PLATFORM_NAMES[b.platform] || b.platform,
              urun: b.urun,
              sku: b.sku,
              altFiyat: b.alt,
              ustFiyat: b.ust,
              paraTipi: (b.paraTipi ?? 0) as CurrencyType,
              emirTipi: b.emirTipi as EmirTipi,
              emirTipiLabel: b.emirTipi === 0 ? t.emirler.buyboxCapture : t.emirler.pressureApply,
              active: b.active,
              workerID: b.workerID,
              status,
              deaktivasyonNedeni: b.deaktivasyonNedeni !== null
                ? DEACTIVATION_REASONS[b.deaktivasyonNedeni] || `Kod: ${b.deaktivasyonNedeni}`
                : null,
              url: b.url,
              priceIncludesCommission: b.priceIncludesCommission ?? true,
              commissionPct: b.commissionPct ?? null,
              modelKodu: b.modelKodu ?? null,
              thumbnail: b.thumbnail ?? null,
              activateCoupon: b.activateCoupon ?? false,
              activatePromotion: b.activatePromotion ?? false,
            };
          });
          setBefels(processedBefels);

          // Update order counts from API response
          setOrderCounts({
            current: befelsResponse.data.current_count ?? 0,
            max: befelsResponse.data.max_count ?? 0,
          });
        }
      }

      setIsLoading(false);
    };

    fetchData();
  }, [token, t.emirler.buyboxCapture, t.emirler.pressureApply]);

  // Calculate filter counts for Befels
  const befelFilterCounts = useMemo(() => {
    const counts = {
      working: 0,
      waiting: 0,
      inactive: 0,
    };
    befels.forEach((b) => {
      counts[b.status]++;
    });
    return {
      ...counts,
      active: counts.working + counts.waiting,
    };
  }, [befels]);

  // Create filter config for Befels
  const befelFilters: BefelFilterConfig[] = [
    { id: 'active', label: t.filters.active, count: befelFilterCounts.active },
    { id: 'working', label: t.emirler.working, count: befelFilterCounts.working },
    { id: 'waiting', label: t.emirler.waiting, count: befelFilterCounts.waiting },
    { id: 'inactive', label: t.emirler.inactive, count: befelFilterCounts.inactive },
  ];

  // Filter handlers for Emirler search
  const handleEmirlerFilterChange = useCallback((field: keyof EmirlerFilters, value: string) => {
    setEmirlerFilters((prev) => ({ ...prev, [field]: value }));
    setEmirlerCurrentPage(1); // Reset to first page when filters change
    // Selection cleanup is handled by useEffect when filteredBefels changes
  }, []);

  const handleClearEmirlerFilters = useCallback(() => {
    setEmirlerFilters({
      urunAdi: '',
      modelKodu: '',
      sku: '',
    });
    setEmirlerCurrentPage(1);
    // Selection cleanup is handled by useEffect when filteredBefels changes
  }, []);

  // Handle page size change for Emirler
  const handleEmirlerPageSizeChange = useCallback((newSize: number) => {
    setEmirlerPerPage(newSize);
    setEmirlerCurrentPage(1); // Reset to first page when page size changes
  }, []);

  // Filter befels based on active filter AND search filters
  const filteredBefels = useMemo(() => {
    let result = befels;

    // Apply status filter
    if (activeFilter === 'active') {
      result = result.filter((b) => b.status === 'working' || b.status === 'waiting');
    } else if (activeFilter !== 'all') {
      result = result.filter((b) => b.status === activeFilter);
    }

    // Apply search filters
    if (emirlerFilters.urunAdi) {
      result = result.filter((b) =>
        b.urun.toLowerCase().includes(emirlerFilters.urunAdi.toLowerCase())
      );
    }
    if (emirlerFilters.modelKodu) {
      result = result.filter((b) =>
        b.modelKodu && b.modelKodu.toLowerCase().includes(emirlerFilters.modelKodu.toLowerCase())
      );
    }
    if (emirlerFilters.sku) {
      result = result.filter((b) =>
        b.sku.toLowerCase().includes(emirlerFilters.sku.toLowerCase())
      );
    }

    return result;
  }, [befels, activeFilter, emirlerFilters]);

  // Paginated befels for display
  const paginatedBefels = useMemo(() => {
    const startIndex = (emirlerCurrentPage - 1) * emirlerPerPage;
    return filteredBefels.slice(startIndex, startIndex + emirlerPerPage);
  }, [filteredBefels, emirlerCurrentPage, emirlerPerPage]);

  // Total pages based on filtered befels
  const emirlerTotalPages = Math.ceil(filteredBefels.length / emirlerPerPage) || 1;

  // Count of selected items by status (for smart button labels)
  const selectedStatusCounts = useMemo(() => {
    const counts = { inactive: 0, active: 0, total: selectedIds.size };
    paginatedBefels.forEach((b) => {
      if (selectedIds.has(b.id)) {
        if (b.status === 'inactive') {
          counts.inactive++;
        } else {
          counts.active++;
        }
      }
    });
    return counts;
  }, [paginatedBefels, selectedIds]);

  // Clean up selection when items are no longer visible on current page
  useEffect(() => {
    const visibleIds = new Set(paginatedBefels.map((b) => b.id));
    setSelectedIds((prev) => {
      const newSet = new Set<string>();
      prev.forEach((id) => {
        if (visibleIds.has(id)) {
          newSet.add(id);
        }
      });
      // Only update if there's a difference
      if (newSet.size !== prev.size) {
        return newSet;
      }
      return prev;
    });
  }, [paginatedBefels]);

  // Selection handlers - only select items on current page
  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === paginatedBefels.length) {
      // Deselect all
      setSelectedIds(new Set());
    } else {
      // Select all on current page
      setSelectedIds(new Set(paginatedBefels.map((b) => b.id)));
    }
  }, [paginatedBefels, selectedIds.size]);

  const handleSelectOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Mass action handlers
  const handleMassDelete = async () => {
    if (!token || selectedIds.size === 0) return;
    if (!confirm(`${selectedIds.size} emir silinecek. Emin misiniz?`)) return;

    setIsMassActionLoading(true);
    const idsToDelete = Array.from(selectedIds);

    // Send requests in batches to avoid overwhelming the server
    const BATCH_SIZE = 10;
    const successIds: string[] = [];

    for (let i = 0; i < idsToDelete.length; i += BATCH_SIZE) {
      const batch = idsToDelete.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map((id) => befelsApi.delete(id, token))
      );
      batch.forEach((id, idx) => {
        if (results[idx].status === 'fulfilled') {
          successIds.push(id);
        }
      });
    }

    setBefels((items) => items.filter((b) => !successIds.includes(b.id)));
    // Update order count after successful deletions
    setOrderCounts((prev) => ({
      ...prev,
      current: Math.max(0, prev.current - successIds.length),
    }));
    setSelectedIds(new Set());
    setIsMassActionLoading(false);
  };

  const handleMassRestart = async () => {
    if (!token || selectedIds.size === 0) return;

    // Only restart inactive befels
    const inactiveBefels = paginatedBefels.filter(
      (b) => selectedIds.has(b.id) && b.status === 'inactive'
    );

    if (inactiveBefels.length === 0) {
      alert('Seçili emirler arasında yeniden başlatılabilecek (devre dışı) emir yok.');
      return;
    }

    setIsMassActionLoading(true);
    const idsToRestart = inactiveBefels.map((b) => b.id);

    // Send requests in batches
    const BATCH_SIZE = 10;
    const successIds: string[] = [];

    for (let i = 0; i < idsToRestart.length; i += BATCH_SIZE) {
      const batch = idsToRestart.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map((id) => befelsApi.restart(id, token))
      );
      batch.forEach((id, idx) => {
        if (results[idx].status === 'fulfilled') {
          successIds.push(id);
        }
      });
    }

    setBefels((items) =>
      items.map((b) =>
        successIds.includes(b.id)
          ? { ...b, active: true, status: 'waiting' as const, workerID: 0, deaktivasyonNedeni: null }
          : b
      )
    );
    setSelectedIds(new Set());
    setIsMassActionLoading(false);
  };

  const handleMassStop = async () => {
    if (!token || selectedIds.size === 0) return;

    // Only stop active befels (working or waiting)
    const activeBefels = paginatedBefels.filter(
      (b) => selectedIds.has(b.id) && (b.status === 'working' || b.status === 'waiting')
    );

    if (activeBefels.length === 0) {
      alert('Seçili emirler arasında durdurulabilecek (aktif) emir yok.');
      return;
    }

    setIsMassActionLoading(true);
    const idsToStop = activeBefels.map((b) => b.id);

    // Send requests in batches
    const BATCH_SIZE = 10;
    const successIds: string[] = [];

    for (let i = 0; i < idsToStop.length; i += BATCH_SIZE) {
      const batch = idsToStop.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map((id) => befelsApi.update(id, token, { active: false }))
      );
      batch.forEach((id, idx) => {
        if (results[idx].status === 'fulfilled') {
          successIds.push(id);
        }
      });
    }

    setBefels((items) =>
      items.map((b) =>
        successIds.includes(b.id)
          ? { ...b, active: false, status: 'inactive' as const, workerID: 0, deaktivasyonNedeni: 'Kullanıcı Durdurdu' }
          : b
      )
    );
    setSelectedIds(new Set());
    setIsMassActionLoading(false);
  };

  // Column headers for Befels
  const befelColumnHeaders: Record<string, string> = {
    checkbox: '',
    thumbnail: '',
    platformDisplayName: t.emirler.platform,
    urun: t.emirler.product,
    sku: t.emirler.sku,
    altFiyat: t.emirler.minPrice,
    ustFiyat: t.emirler.maxPrice,
    commissionPct: 'Komisyon',
    emirTipiLabel: t.emirler.orderType,
    status: t.emirler.status,
    deaktivasyonNedeni: t.emirler.deactivationReason,
    actions: t.emirler.actions || 'İşlemler',
  };

  // Define columns for Befels table (includes renderers inline to avoid stale closures)
  const befelColumns: ColumnConfig<ProcessedBefel>[] = useMemo(() => {
    const statusColors: Record<string, string> = {
      working: 'var(--color-success)',
      waiting: 'var(--color-warning)',
      inactive: 'var(--color-danger)',
    };
    const statusLabels: Record<string, string> = {
      working: t.emirler.working,
      waiting: t.emirler.waiting,
      inactive: t.emirler.inactive,
    };

    const renderers: Record<string, (value: unknown, item: ProcessedBefel) => React.ReactNode> = {
      checkbox: (_, item) => (
        <input
          type="checkbox"
          checked={selectedIds.has(item.id)}
          onChange={() => handleSelectOne(item.id)}
          onClick={(e) => e.stopPropagation()}
          className={styles.rowCheckbox}
        />
      ),
      thumbnail: (value) => {
        const url = value as string | null;
        if (!url) {
          return (
            <div
              style={{
                width: '40px',
                height: '40px',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-tertiary)',
                fontSize: '10px',
              }}
            >
              ?
            </div>
          );
        }
        return (
          <img
            src={url}
            alt=""
            style={{
              width: '40px',
              height: '40px',
              objectFit: 'cover',
              borderRadius: '4px',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        );
      },
      platformDisplayName: (value, item) => {
        const name = value as string;
        const url = PLATFORM_URLS[item.platform];
        if (url) {
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--color-primary)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >
              {name}
            </a>
          );
        }
        return name;
      },
      status: (value) => {
        const status = value as string;
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 8px',
              marginLeft: '-8px',
              borderRadius: '4px',
              fontWeight: 500,
              backgroundColor: `${statusColors[status]}20`,
              color: statusColors[status],
            }}
          >
            {statusLabels[status]}
          </span>
        );
      },
      emirTipiLabel: (value) => {
        const label = value as string;
        const isBuybox = label === t.emirler.buyboxCapture;
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 8px',
              marginLeft: '-8px',
              borderRadius: '4px',
              fontWeight: 500,
              backgroundColor: isBuybox ? 'var(--color-info)20' : 'var(--color-warning)20',
              color: isBuybox ? 'var(--color-info)' : 'var(--color-warning)',
            }}
          >
            {label}
          </span>
        );
      },
      urun: (value, item) => {
        const name = value as string;
        if (item.url) {
          return (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--color-primary)',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >
              {name}
            </a>
          );
        }
        return name;
      },
      sku: (value) => {
        const sku = value as string;
        // Truncate if too long
        if (sku && sku.length > 20) {
          return <span title={sku}>{sku.substring(0, 20)}...</span>;
        }
        return sku;
      },
      altFiyat: (value, item) => {
        const price = `${CURRENCY_SYMBOLS[item.paraTipi]}${(value as number).toFixed(2)}`;
        if (!item.priceIncludesCommission && item.commissionPct) {
          return (
            <span title={`Komisyon dahil değil (%${item.commissionPct.toFixed(1)})`}>
              {price} <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>*</span>
            </span>
          );
        }
        return price;
      },
      ustFiyat: (value, item) => {
        const price = `${CURRENCY_SYMBOLS[item.paraTipi]}${(value as number).toFixed(2)}`;
        if (!item.priceIncludesCommission && item.commissionPct) {
          return (
            <span title={`Komisyon dahil değil (%${item.commissionPct.toFixed(1)})`}>
              {price} <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>*</span>
            </span>
          );
        }
        return price;
      },
      commissionPct: (value, item) => {
        if (value === null || value === undefined) {
          return <span style={{ color: 'var(--text-tertiary)' }}>-</span>;
        }
        const pct = (value as number).toFixed(1);
        const includesCommission = item.priceIncludesCommission;
        return (
          <span
            title={includesCommission ? 'Fiyatlar komisyon dahil' : 'Fiyatlar komisyon hariç'}
            style={{ color: includesCommission ? 'var(--text-secondary)' : 'var(--color-warning)' }}
          >
            %{pct}{!includesCommission && ' *'}
          </span>
        );
      },
      deaktivasyonNedeni: (value, item) => {
        // Show reason for inactive befels AND market standby befels (which show as working)
        // Market standby befels have deaktivasyonNedeni set even though they show as working
        if (item.status === 'inactive') {
          return value ? String(value) : '-';
        }
        // For working/waiting befels, show market standby reason with link to info page
        if (value && String(value).includes('Pazar')) {
          return (
            <a
              href="/market-standby"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--color-info)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              title="Pazar bekleme modu hakkında bilgi al"
            >
              {String(value)}
            </a>
          );
        }
        return '-';
      },
      actions: (_, item) => {
        const isExpired = user?.isExpired;
        return (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {/* Restart button - only for inactive befels and non-expired users */}
            {item.status === 'inactive' && !isExpired && (
              <button
                onClick={() => handleRestartBefel(item)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: 'var(--color-success)',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {t.emirler.restart}
              </button>
            )}
            {/* Stop button - for active befels (working or waiting), available for all users including expired */}
            {item.status !== 'inactive' && (
              <button
                onClick={() => handleStopBefel(item)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: 'var(--color-warning)',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {t.emirler.stop}
              </button>
            )}
            {/* Edit button - only for non-expired users */}
            {!isExpired && (
              <button
                onClick={() => handleEditClick(item)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: 'var(--color-info)',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {t.edit}
              </button>
            )}
            {/* Delete button - always available */}
            <button
              onClick={async () => {
                if (!token) return;
                const response = await befelsApi.delete(item.id, token);
                if (response.data?.success) {
                  setBefels((items) => items.filter((b) => b.id !== item.id));
                  // Update order count after successful deletion
                  setOrderCounts((prev) => ({
                    ...prev,
                    current: Math.max(0, prev.current - 1),
                  }));
                } else {
                  console.error('Failed to delete befel:', response.error);
                }
              }}
              style={{
                padding: '4px 10px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'var(--color-danger)',
                color: 'white',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {t.emirler.delete}
            </button>
          </div>
        );
      },
    };

    const columnConfigs: Array<{ key: string; width?: string; className?: string }> = [
      { key: 'checkbox', width: '40px' },
      { key: 'thumbnail', width: '50px' },
      { key: 'platformDisplayName', width: '100px' },
      { key: 'urun' },
      { key: 'sku', width: '180px' },
      { key: 'altFiyat', width: '90px' },
      { key: 'ustFiyat', width: '90px' },
      { key: 'commissionPct', width: '80px' },
      { key: 'emirTipiLabel', width: '80px' },
      { key: 'status', width: '90px' },
      { key: 'deaktivasyonNedeni', width: '120px' },
      { key: 'actions', width: '180px', className: styles.actionCell },
    ];

    return columnConfigs.map((col) => ({
      key: col.key,
      header: befelColumnHeaders[col.key] || col.key,
      render: renderers[col.key],
      width: col.width,
      className: col.className,
    }));
  }, [t, token, user, befelColumnHeaders, selectedIds, handleSelectOne, setOrderCounts]);

  // API Key handlers
  const handleDeleteApiKey = async (id: string) => {
    if (!token) return;

    const response = await apiKeysApi.delete(id, token);
    if (response.data?.success) {
      setApiKeys((keys) => keys.filter((k) => k.id !== id));
    } else {
      console.error('Failed to delete API key:', response.error);
    }
  };

  // Edit modal handlers
  const handleEditClick = (befel: ProcessedBefel) => {
    setEditingBefel(befel);
    setEditFormData({
      alt: befel.altFiyat.toString(),
      ust: befel.ustFiyat.toString(),
      emirTipi: befel.emirTipi,
      paraTipi: befel.paraTipi,
      priceIncludesCommission: befel.priceIncludesCommission,
      activateCoupon: befel.activateCoupon,
      activatePromotion: befel.activatePromotion,
    });
    setEditMessage(null);
    setIsEditModalOpen(true);
  };

  const handleEditClose = () => {
    setIsEditModalOpen(false);
    setEditingBefel(null);
    setEditFormData({ alt: '', ust: '', emirTipi: 0, paraTipi: 0, priceIncludesCommission: true, activateCoupon: false, activatePromotion: false });
    setEditMessage(null);
  };

  const handleEditSubmit = async () => {
    if (!editingBefel || !token) return;

    // Validate Alt < Ust
    const alt = parseFloat(editFormData.alt);
    const ust = parseFloat(editFormData.ust);
    if (!isNaN(alt) && !isNaN(ust) && alt >= ust) {
      setEditMessage({ type: 'error', text: t.addOrders.minPriceGreaterThanMax });
      return;
    }

    setIsEditSubmitting(true);
    const response = await befelsApi.update(editingBefel.id, token, {
      alt: parseFloat(editFormData.alt),
      ust: parseFloat(editFormData.ust),
      emirTipi: editFormData.emirTipi,
      paraTipi: editFormData.paraTipi,
      priceIncludesCommission: editFormData.priceIncludesCommission,
      activateCoupon: editFormData.activateCoupon,
      activatePromotion: editFormData.activatePromotion,
    });
    setIsEditSubmitting(false);

    if (response.data) {
      // Update local state
      setBefels((items) =>
        items.map((b) =>
          b.id === editingBefel.id
            ? {
                ...b,
                altFiyat: parseFloat(editFormData.alt),
                ustFiyat: parseFloat(editFormData.ust),
                emirTipi: editFormData.emirTipi,
                emirTipiLabel: editFormData.emirTipi === 0 ? t.emirler.buyboxCapture : t.emirler.pressureApply,
                paraTipi: editFormData.paraTipi,
                priceIncludesCommission: editFormData.priceIncludesCommission,
                activateCoupon: editFormData.activateCoupon,
                activatePromotion: editFormData.activatePromotion,
              }
            : b
        )
      );
      setEditMessage({ type: 'success', text: t.emirler.orderUpdated });
      setTimeout(handleEditClose, 1500);
    } else {
      setEditMessage({ type: 'error', text: response.error || t.emirler.orderUpdateError });
    }
  };

  // Restart handler
  const handleRestartBefel = async (befel: ProcessedBefel) => {
    if (!token) return;

    const response = await befelsApi.restart(befel.id, token);
    if (response.data) {
      // Update local state - set to waiting status
      setBefels((items) =>
        items.map((b) =>
          b.id === befel.id
            ? { ...b, active: true, status: 'waiting' as const, workerID: 0, deaktivasyonNedeni: null }
            : b
        )
      );
    } else {
      console.error('Failed to restart befel:', response.error);
    }
  };

  // Stop handler
  const handleStopBefel = async (befel: ProcessedBefel) => {
    if (!token) return;

    const response = await befelsApi.update(befel.id, token, { active: false });
    if (response.data) {
      // Update local state - set to inactive status with user stopped reason
      setBefels((items) =>
        items.map((b) =>
          b.id === befel.id
            ? { ...b, active: false, status: 'inactive' as const, workerID: 0, deaktivasyonNedeni: 'Kullanıcı Durdurdu' }
            : b
        )
      );
    } else {
      console.error('Failed to stop befel:', response.error);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <p>{t.loading}</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t.dashboard.title}</h1>
      </header>

      <section className={styles.section}>
        <ApiKeysManager
          apiKeys={apiKeys}
          onDelete={handleDeleteApiKey}
        />
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            {t.emirler.title}
            <Tooltip text={t.paymentPlans.tooltipText} linkTo="/payment-plans">
              <span className={styles.orderCount}>
                ({orderCounts.current}/{orderCounts.max})
              </span>
            </Tooltip>
          </h2>
          {!user?.isExpired && (
            <Link to="/add-orders" className={styles.addOrderBtn}>
              {t.emirler.addOrder}
            </Link>
          )}
        </div>

        <FilterTabs
          filters={befelFilters}
          activeFilter={activeFilter}
          onFilterChange={(filter) => {
            setActiveFilter(filter as BefelFilterStatus);
            setEmirlerCurrentPage(1); // Reset to first page on filter change
            // Selection cleanup is handled by useEffect when filteredBefels changes
          }}
          totalCount={befels.length}
        />

        {/* Search Filters */}
        <div className={styles.emirlerFilters}>
          <div className={styles.filterRow}>
            <div className={styles.filterField}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Ürün Adı"
                value={emirlerFilters.urunAdi}
                onChange={(e) => handleEmirlerFilterChange('urunAdi', e.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Model Kodu"
                value={emirlerFilters.modelKodu}
                onChange={(e) => handleEmirlerFilterChange('modelKodu', e.target.value)}
              />
            </div>
            <div className={styles.filterField}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="SKU / Stok Kodu"
                value={emirlerFilters.sku}
                onChange={(e) => handleEmirlerFilterChange('sku', e.target.value)}
              />
            </div>
            <div className={styles.filterActions}>
              <button
                className={styles.clearFilterBtn}
                onClick={handleClearEmirlerFilters}
              >
                Temizle
              </button>
            </div>
          </div>
        </div>

        {/* Pagination Header */}
        <div className={styles.paginationHeader}>
          <div className={styles.selectAllContainer}>
            <input
              type="checkbox"
              checked={paginatedBefels.length > 0 && selectedIds.size === paginatedBefels.length}
              onChange={handleSelectAll}
              className={styles.selectAllCheckbox}
              disabled={paginatedBefels.length === 0}
            />
            <span className={styles.selectionCount}>
              {selectedIds.size > 0
                ? `${selectedIds.size} seçili`
                : `${filteredBefels.length} emir`}
            </span>
          </div>

          {selectedIds.size > 0 && (
            <div className={styles.massActionButtons}>
              {!user?.isExpired && selectedStatusCounts.inactive > 0 && (
                <button
                  className={`${styles.massActionBtn} ${styles.massRestartBtn}`}
                  onClick={handleMassRestart}
                  disabled={isMassActionLoading}
                  title="Seçili devre dışı emirleri yeniden başlat"
                >
                  Yeniden Başlat ({selectedStatusCounts.inactive})
                </button>
              )}
              {selectedStatusCounts.active > 0 && (
                <button
                  className={`${styles.massActionBtn} ${styles.massStopBtn}`}
                  onClick={handleMassStop}
                  disabled={isMassActionLoading}
                  title="Seçili aktif emirleri durdur"
                >
                  Durdur ({selectedStatusCounts.active})
                </button>
              )}
              <button
                className={`${styles.massActionBtn} ${styles.massDeleteBtn}`}
                onClick={handleMassDelete}
                disabled={isMassActionLoading}
                title="Seçili emirleri sil"
              >
                Sil ({selectedIds.size})
              </button>
            </div>
          )}

          <div className={styles.paginationControls}>
            <span className={styles.pageInfoText}>Her Sayfada</span>
            <select
              className={styles.pageSizeSelect}
              value={emirlerPerPage}
              onChange={(e) => handleEmirlerPageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <div className={styles.paginationButtons}>
              <button
                className={styles.pageBtn}
                onClick={() => setEmirlerCurrentPage((p) => Math.max(1, p - 1))}
                disabled={emirlerCurrentPage <= 1}
              >
                &lt;
              </button>
              <span className={styles.currentPageIndicator}>{emirlerCurrentPage}</span>
              {emirlerTotalPages > 1 && (
                <span className={styles.totalPagesText}>/ {emirlerTotalPages}</span>
              )}
              <button
                className={styles.pageBtn}
                onClick={() => setEmirlerCurrentPage((p) => Math.min(emirlerTotalPages, p + 1))}
                disabled={emirlerCurrentPage >= emirlerTotalPages}
              >
                &gt;
              </button>
            </div>
          </div>
        </div>

        <DataTable
          data={paginatedBefels}
          columns={befelColumns}
          keyField="id"
          emptyMessage={filteredBefels.length === 0 && (emirlerFilters.urunAdi || emirlerFilters.modelKodu || emirlerFilters.sku)
            ? 'Filtreye uygun emir bulunamadı'
            : t.emirler.noEmirlerMatch}
        />

        {/* Bottom Pagination */}
        {emirlerTotalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              onClick={() => setEmirlerCurrentPage(1)}
              disabled={emirlerCurrentPage <= 1}
              title="İlk Sayfa"
            >
              &laquo;
            </button>
            <button
              className={styles.pageBtn}
              onClick={() => setEmirlerCurrentPage((p) => Math.max(1, p - 1))}
              disabled={emirlerCurrentPage <= 1}
              title="Önceki"
            >
              &larr;
            </button>

            <span className={styles.pageInfo}>
              <input
                type="number"
                className={styles.pageInput}
                value={emirlerCurrentPage}
                min={1}
                max={emirlerTotalPages}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1 && val <= emirlerTotalPages) {
                    setEmirlerCurrentPage(val);
                  }
                }}
              />
              <span className={styles.pageTotal}>/ {emirlerTotalPages}</span>
              <span className={styles.productCount}>
                ({filteredBefels.length} emir)
              </span>
            </span>

            <button
              className={styles.pageBtn}
              onClick={() => setEmirlerCurrentPage((p) => Math.min(emirlerTotalPages, p + 1))}
              disabled={emirlerCurrentPage >= emirlerTotalPages}
              title="Sonraki"
            >
              &rarr;
            </button>
            <button
              className={styles.pageBtn}
              onClick={() => setEmirlerCurrentPage(emirlerTotalPages)}
              disabled={emirlerCurrentPage >= emirlerTotalPages}
              title="Son Sayfa"
            >
              &raquo;
            </button>
          </div>
        )}
      </section>

      {/* Edit Modal */}
      {isEditModalOpen && editingBefel && (
        <div className={styles.modalOverlay} onClick={handleEditClose}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{t.emirler.editOrder}</h2>
              <button className={styles.closeBtn} onClick={handleEditClose}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* Product Info (read-only) */}
              <div className={styles.formSection}>
                <h3>{t.addOrders.productInfo}</h3>
                <div className={styles.productInfoCard}>
                  <div className={styles.productInfoRow}>
                    <span className={styles.label}>{t.emirler.platform}:</span>
                    <span>{editingBefel.platformDisplayName}</span>
                  </div>
                  <div className={styles.productInfoRow}>
                    <span className={styles.label}>{t.emirler.product}:</span>
                    <span>{editingBefel.urun}</span>
                  </div>
                  <div className={styles.productInfoRow}>
                    <span className={styles.label}>{t.emirler.sku}:</span>
                    <span>{editingBefel.sku}</span>
                  </div>
                </div>
              </div>

              {/* Editable fields */}
              <div className={styles.formSection}>
                <h3>{t.addOrders.priceSettings}</h3>

                {/* Currency */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t.addOrders.currency}</label>
                  <select
                    className={styles.formSelect}
                    value={editFormData.paraTipi}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, paraTipi: parseInt(e.target.value) as CurrencyType })
                    }
                  >
                    <option value={0}>{t.addOrders.currencyTRY}</option>
                    <option value={1}>{t.addOrders.currencyUSD}</option>
                    <option value={2}>{t.addOrders.currencyEUR}</option>
                    <option value={3}>{t.addOrders.currencyYUAN}</option>
                  </select>
                </div>

                {/* Min/Max Price */}
                <div className={styles.priceInputs}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t.addOrders.minPrice}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={styles.formInput}
                      value={editFormData.alt}
                      onChange={(e) => setEditFormData({ ...editFormData, alt: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t.addOrders.maxPrice}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={styles.formInput}
                      value={editFormData.ust}
                      onChange={(e) => setEditFormData({ ...editFormData, ust: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Order Type */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t.addOrders.orderType}</label>
                  <select
                    className={styles.formSelect}
                    value={editFormData.emirTipi}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, emirTipi: parseInt(e.target.value) as EmirTipi })
                    }
                  >
                    <option value={0}>{t.addOrders.buyboxCapture}</option>
                    <option value={1}>{t.addOrders.pressureApply}</option>
                  </select>
                </div>

                {/* Commission included checkbox */}
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={editFormData.priceIncludesCommission}
                      onChange={(e) => setEditFormData({ ...editFormData, priceIncludesCommission: e.target.checked })}
                      className={styles.checkbox}
                    />
                    <span>{t.addOrders.priceIncludesCommission}</span>
                  </label>
                </div>

                {/* Coupon activation checkbox */}
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={editFormData.activateCoupon}
                      onChange={(e) => setEditFormData({ ...editFormData, activateCoupon: e.target.checked })}
                      className={styles.checkbox}
                    />
                    <span>{t.addOrders.activateCoupon}</span>
                  </label>
                </div>

                {/* Promotion activation checkbox */}
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={editFormData.activatePromotion}
                      onChange={(e) => setEditFormData({ ...editFormData, activatePromotion: e.target.checked })}
                      className={styles.checkbox}
                    />
                    <span>{t.addOrders.activatePromotion}</span>
                  </label>
                </div>

                {/* Pricing hint */}
                <div className={styles.pricingHint}>
                  {t.addOrders.pricingHint}
                </div>
              </div>

              {editMessage && (
                <div className={`${styles.message} ${styles[editMessage.type]}`}>{editMessage.text}</div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={handleEditClose} disabled={isEditSubmitting}>
                {t.cancel}
              </button>
              <button className={styles.submitBtn} onClick={handleEditSubmit} disabled={isEditSubmitting}>
                {isEditSubmitting ? t.loading : t.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
